import React, { useState } from "react";
import { X, CircleNotch, PaperPlaneRight, Trash, SignIn } from "@phosphor-icons/react";
import AuthModal from "@/components/AuthModal";

export function TopicDetailModal({
  topic,
  onClose,
  replies,
  loadingReplies,
  newReply,
  onReplyChange,
  onSubmitReply,
  submitting,
  onDeleteReply,
  user,
  formatDate
}) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  if (!topic) return null;

  const canDeleteReply = (reply) => {
    if (!user) return false;
    return reply.user_id === user.id || user.role === "admin";
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#E5E7EB] flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-0.5 bg-[#0F766E] text-white text-[10px] font-mono uppercase">
                {topic.category}
              </span>
              {topic.region && topic.region !== "global" && (
                <span className="px-2 py-0.5 bg-[#E5E7EB] text-[#4B5563] text-[10px] font-mono uppercase">
                  {topic.region}
                </span>
              )}
            </div>
            <h2 className="font-serif text-xl text-[#111827]">{topic.title}</h2>
            <p className="text-xs text-[#6B7280] mt-1">
              by {topic.author_name} • {formatDate(topic.created_at)}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#6B7280] hover:text-[#111827]">
            <X size={20} />
          </button>
        </div>

        {/* Original Post */}
        <div className="p-4 border-b border-[#E5E7EB] bg-[#F9FAFB]">
          <p className="text-[#4B5563] whitespace-pre-wrap">{topic.content}</p>
        </div>

        {/* Replies */}
        <div className="flex-1 overflow-y-auto">
          {loadingReplies ? (
            <div className="p-8 text-center">
              <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto" />
            </div>
          ) : replies?.length === 0 ? (
            <div className="p-8 text-center text-[#6B7280]">
              No replies yet. Be the first to respond!
            </div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {replies?.map((reply) => (
                <div key={reply.id} className="p-4">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 bg-[#E5E7EB] text-[#4B5563] flex items-center justify-center font-medium flex-shrink-0">
                      {reply.user_name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#111827]">{reply.user_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#6B7280]">{formatDate(reply.created_at)}</span>
                          {canDeleteReply(reply) && (
                            <button
                              onClick={() => onDeleteReply(reply.id)}
                              className="text-[#6B7280] hover:text-[#DC2626] transition-colors"
                              title="Delete reply"
                            >
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-[#4B5563] mt-2 whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply Form */}
        {user ? (
          <div className="p-4 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <textarea
              placeholder="Write a reply..."
              value={newReply}
              onChange={(e) => onReplyChange(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-[#E5E7EB] text-sm resize-none focus:outline-none focus:border-[#0F766E]"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={onSubmitReply}
                disabled={submitting || !newReply.trim()}
                className="px-4 py-2 bg-[#0F766E] text-white hover:bg-[#14B8A6] disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? <CircleNotch size={18} className="animate-spin" /> : <PaperPlaneRight size={18} />}
                Post Reply
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 border-t border-[#E5E7EB] text-center">
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] transition-colors text-sm"
            >
              <SignIn size={18} />
              Sign in to reply
            </button>
          </div>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

export default TopicDetailModal;
